import React, { useContext } from 'react';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FcGoogle } from 'react-icons/fc';

import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { auth, googleProvider } from 'utilities/firebase';

import { ICARDIO_AI_LOGO } from 'constants/images';
import Button from '../Button';
import Input from '../Input';
import {
  ContainerBodyWrapper,
  ICardioLogo,
  LoginForm,
  LoginFormControl,
  LoginFormControlItem,
  LoginFormControlLabel,
} from './Login.style';
import { useLoginMutation } from '../../redux/slices/auth';
import { UserContext } from 'services/contexts';
import { IUser } from '../../models/auth';

const Login = () => {
  const navigate = useNavigate();
  const [loginFn, { isLoading: isLoginLoading }] = useLoginMutation();
  const { setUser } = useContext(UserContext);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    trigger,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async data => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);
      const user = userCredential.user;
      const accessToken = await user.getIdToken();
      if (user && accessToken) {
        loginFn(accessToken);
        const data = {
          token: accessToken,
          profile: {
            email: user.email,
            firstName: user.displayName,
            isSuperuser: false,
            lastName: '',
          },
          email: user.email,
        };
        setUser(data as IUser);
        localStorage.setItem('user', JSON.stringify(data));
        localStorage.setItem('token', accessToken);
        navigate(`/`);
      }
    } catch (e) {
      toast.error('Invalid Credentials');
    }
  };

  useEffect(() => {
    const listener = event => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        trigger().then(rs => {
          if (rs) {
            const { email, password } = getValues();
            onSubmit({
              email,
              password,
            });
          }
        });
        event.preventDefault();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, []);

  const onGoogleAuth = async () => {
    try {
      const firebaseAuthData = await signInWithPopup(auth, googleProvider);
      const user = firebaseAuthData.user;
      const accessToken = await user.getIdToken();
      if (user && accessToken) {
        loginFn(accessToken);
        const data = {
          token: accessToken,
          profile: {
            email: user.email,
            firstName: user.displayName,
            isSuperuser: false,
            lastName: '',
          },
          email: user.email,
        };
        setUser(data as IUser);
        localStorage.setItem('user', JSON.stringify(data));
        localStorage.setItem('token', accessToken);
        navigate(`/`);
      }
    } catch (e) {
      // intentionally left blank
    }
  };

  return (
    <ContainerBodyWrapper>
      <LoginForm onSubmit={handleSubmit(onSubmit)}>
        <ICardioLogo src={ICARDIO_AI_LOGO} alt="logo" />

        <LoginFormControl>
          <LoginFormControlLabel>Enter your email</LoginFormControlLabel>
          <LoginFormControlItem>
            <Input
              placeholder="Email"
              {...register('email', {
                required: 'Please enter your email address',
                pattern: {
                  value:
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  message: 'Invalid email address',
                },
              })}
              errors={errors?.email?.message}
              onChange={e => {
                setValue('email', e.target.value, { shouldValidate: true });
              }}
            />
          </LoginFormControlItem>
        </LoginFormControl>
        <LoginFormControl>
          <LoginFormControlLabel>Enter your password</LoginFormControlLabel>
          <LoginFormControlItem>
            <Input
              placeholder="Password"
              type="password"
              {...register('password', { required: 'Please enter your password' })}
              errors={errors?.password?.message}
              onChange={e => {
                setValue('password', e.target.value, { shouldValidate: true });
              }}
            />
          </LoginFormControlItem>
          <Button
            style={{
              marginTop: '40px',
              width: '100%',
            }}
            fullwidth
            label={'Login'}
            buttonType="default"
            type="submit"
            loading={isLoginLoading}
            icon={null}
          />
        </LoginFormControl>
        <Button
          style={{
            marginTop: '25px',
            width: '100%',
          }}
          fullwidth
          label={'Login with Google'}
          buttonType="default"
          onClick={onGoogleAuth}
          icon={<FcGoogle size={24} style={{ marginRight: 14 }} />}
          loading={isLoginLoading}
        />
      </LoginForm>
    </ContainerBodyWrapper>
  );
};

export default Login;
